import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { HttpClient, HttpEventType } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { filter, tap } from 'rxjs/operators';

@Component({
  selector: 'app-excel-upload',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './excel-upload.component.html',
  styleUrls: ['./excel-upload.component.scss'],
})
export class ExcelUploadComponent {
  private http = inject(HttpClient);

  @Input() accept: string = '.xlsx,.xls'; // allowed file types
  @Input() uploadUrl!: string; // API endpoint passed from parent

  @Output() uploadSuccess = new EventEmitter<any>(); // emit API response
  @Output() uploadError = new EventEmitter<any>(); // emit API error

  message = '';
  modalType: 'success' | 'error' | 'confirm' = 'success';
  showModal = false;
  progress = 0; // Tracks upload progress percentage

  private selectedFile: File | null = null;

  // File input change
  onFileChange(event: any): void {
    const target: DataTransfer = <DataTransfer>event.target;

    if (target.files.length !== 1) {
      this.showPopup('Please select only one file.', 'error');
      return;
    }

    const file = target.files[0];

    // Added MIME type validation for better security
    const allowedMimeTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel', // .xls
    ];

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      this.showPopup(
        'Invalid file format. Please upload an Excel file.',
        'error'
      );
      return;
    }

    if (!allowedMimeTypes.includes(file.type)) {
      this.showPopup(
        'Invalid file type. Please upload a valid Excel file.',
        'error'
      );
      return;
    }

    // Save file and ask confirmation
    this.selectedFile = file;
    this.showPopup(
      `Are you sure you want to upload "${file.name}"?`,
      'confirm'
    );
  }

  // Confirm upload → send file to API with progress tracking
  confirmUpload(): void {
    if (!this.selectedFile || !this.uploadUrl) return;

    this.progress = 0; // Reset progress bar before upload starts
    const formData = new FormData();
    formData.append('file', this.selectedFile);

    this.http
      .post(this.uploadUrl, formData, {
        reportProgress: true, // This is crucial for tracking progress
        observe: 'events',
      })
      .pipe(
        // Use the tap operator to handle progress events
        tap((event) => {
          if (event.type === HttpEventType.UploadProgress) {
            this.progress = Math.round(
              (100 * event.loaded) / (event.total || 1)
            );
          }
        }),
        // Filter for the final response event
        filter((event) => event.type === HttpEventType.Response)
      )
      .subscribe({
        next: (res) => {
          this.uploadSuccess.emit(res);
          this.showPopup('Excel file uploaded successfully!', 'success');
          this.selectedFile = null;
          this.progress = 100; // Ensure progress bar is full on success
        },
        error: (err) => {
          this.uploadError.emit(err);
          this.showPopup('Failed to upload file.', 'error');
          this.progress = 0; // Reset progress bar on error
        },
      });
  }

  // Show popup
  private showPopup(message: string, type: 'success' | 'error' | 'confirm') {
    this.message = message;
    this.modalType = type;
    this.showModal = true;
  }

  // Close popup
  closePopup() {
    this.showModal = false;
    this.message = '';
  }
}
